import { getDocument, getWindow } from "@thelabnyc/thelabui/src/utils/dom";

import { isElement } from "./guards";

/** Shared focusable selector used by traps and programmatic focus helpers. */
const FOCUSABLE_SELECTOR = [
    "a[href]:not([disabled])",
    "button:not([disabled])",
    "textarea:not([disabled])",
    'input:not([disabled]):not([type="hidden"])',
    "select:not([disabled])",
].join(", ");

const focusableElemSelectorWithTabindex = [
    FOCUSABLE_SELECTOR,
    '[tabindex]:not([tabindex="-1"])',
].join(", ");

type GetFocusableElemsOptions = {
    /**
     * Include elements with an explicit non-negative tabindex (not only native
     * controls). Needed for dialog shells and custom widgets.
     */
    includeExplicitTabindex?: boolean;
};

/** Elements with tabindex="-1" are excluded from focus-trap traversal. */
export const getFocusableElems = (
    parent: HTMLElement,
    options?: GetFocusableElemsOptions,
): HTMLElement[] => {
    const selector = options?.includeExplicitTabindex
        ? focusableElemSelectorWithTabindex
        : FOCUSABLE_SELECTOR;
    const window = getWindow();
    if (!window) {
        return [];
    }
    const elems = Array.from(
        parent.querySelectorAll<HTMLElement>(selector),
    ).filter((el) => {
        if (
            el.tabIndex < 0 ||
            el.closest("[disabled]") ||
            el.closest("[inert]")
        ) {
            return false;
        }
        // Prefer getClientRects over offsetParent so position:fixed controls
        // remain eligible (offsetParent is null for fixed elements).
        const style = window.getComputedStyle(el);
        return (
            style.getPropertyValue("visibility") !== "hidden" &&
            style.getPropertyValue("display") !== "none" &&
            el.getClientRects().length > 0
        );
    });
    // Sort by DOM order
    return elems.sort((a, b) => {
        const position = a.compareDocumentPosition(b);
        if (position & Node.DOCUMENT_POSITION_FOLLOWING) return -1;
        if (position & Node.DOCUMENT_POSITION_PRECEDING) return 1;
        return 0;
    });
};

export const trapFocus = (
    element: HTMLElement,
    options?: GetFocusableElemsOptions,
): (() => void) => {
    const document = getDocument();

    const handleKeyDown = (event: KeyboardEvent) => {
        if (!document) {
            return;
        }
        if (event.key !== "Tab") {
            return;
        }

        const focusableElems = getFocusableElems(element, options);
        if (focusableElems.length === 0) {
            return;
        }
        const firstFocusableEl = focusableElems[0];
        const lastFocusableEl = focusableElems[focusableElems.length - 1];
        const activeElement = document.activeElement;
        if (!activeElement) {
            return;
        }
        if (
            !(activeElement instanceof HTMLElement) ||
            !element.contains(activeElement)
        ) {
            return;
        }

        if (event.shiftKey) {
            const hasPrev = focusableElems.some(
                (el) =>
                    el !== activeElement &&
                    (activeElement.compareDocumentPosition(el) &
                        Node.DOCUMENT_POSITION_PRECEDING) !==
                        0,
            );
            if (!hasPrev) {
                focusElement(lastFocusableEl);
                event.preventDefault();
            }
        } else {
            const hasNext = focusableElems.some(
                (el) =>
                    el !== activeElement &&
                    (activeElement.compareDocumentPosition(el) &
                        Node.DOCUMENT_POSITION_FOLLOWING) !==
                        0,
            );
            if (!hasNext) {
                focusElement(firstFocusableEl);
                event.preventDefault();
            }
        }
    };

    element.addEventListener("keydown", handleKeyDown);

    // Return cleanup function
    return () => {
        element.removeEventListener("keydown", handleKeyDown);
    };
};

type FocusOptionsWithVisible = FocusOptions & {
    /**
     * Forces the focus ring regardless of input modality, so it defeats a
     * `:focus-visible` rule rather than complementing one -- pass it only where
     * the ring is wanted for mouse users as well. Scripted focus moves that
     * follow a keyboard-activated control already match :focus-visible via the
     * browser's own heuristic, so they do not need it.
     */
    focusVisible?: boolean;
};

type TemporaryFocusOwner = {
    restoreTabIndex: () => void;
    onBlur: () => void;
};

const temporaryFocusOwners = new WeakMap<HTMLElement, TemporaryFocusOwner>();

/**
 * Focus a non-interactive element by temporarily setting tabindex="-1".
 * Restores or removes the attribute on blur so programmatic focus targets
 * do not retain tabindex in the DOM after the user moves on.
 *
 * Re-entrant: a second call on the same still-focused element replaces the
 * prior blur cleanup and reuses the original pre-call tabindex restore, so
 * overlapping calls cannot leave a sticky tabindex="-1".
 */
export const focusWithTemporaryTabIndex = (
    element: HTMLElement,
    options?: FocusOptionsWithVisible,
): void => {
    const existing = temporaryFocusOwners.get(element);
    if (existing) {
        element.removeEventListener("blur", existing.onBlur);
        temporaryFocusOwners.delete(element);
    }

    const restoreTabIndex =
        existing?.restoreTabIndex ??
        (() => {
            const hadTabIndex = element.hasAttribute("tabindex");
            const previousTabIndex = element.tabIndex;
            return (): void => {
                if (hadTabIndex) {
                    element.tabIndex = previousTabIndex;
                } else {
                    element.removeAttribute("tabindex");
                }
            };
        })();

    const onBlur = (): void => {
        const owner = temporaryFocusOwners.get(element);
        if (owner?.onBlur !== onBlur) {
            return;
        }
        temporaryFocusOwners.delete(element);
        element.removeEventListener("blur", onBlur);
        restoreTabIndex();
    };

    element.tabIndex = -1;
    focusElement(element, options);

    const document = getDocument();
    if (document?.activeElement === element) {
        temporaryFocusOwners.set(element, { restoreTabIndex, onBlur });
        element.addEventListener("blur", onBlur);
    } else {
        restoreTabIndex();
    }
};

export const focusElement = (
    selector: HTMLElement | string,
    options?: FocusOptionsWithVisible,
) => {
    const document = getDocument();
    if (!document) {
        return;
    }
    const elem = isElement(selector)
        ? selector
        : document.querySelector<HTMLElement>(selector);
    if (elem) {
        console.trace(`Changing keyboard focus to ${selector}.`);
        elem.focus(options);
    } else {
        console.trace(
            `Attempted to change keyboard focus to ${selector}, but no matching element exists.`,
        );
    }
};
